/**
 * CatalogIssuesTeamsMessage — turns the open Operator Catalog issues into a plain-language Teams
 * message for whoever fixes them in Core Director (Bill 2026-10-06: "generate a teams message with
 * the details so that I can direct Scott and Amy to these for resolution with plain language
 * instructions as to what needs to happen").
 *
 * Governance (Bill 2026-10-06): Entra governs title, name and e-mail — Core Director must match it
 * exactly, and the Core Operator Numbers SharePoint list is updated after Core. Duplicate initials are
 * resolved in Core Director first, then the SharePoint list. Where Entra itself is wrong (a typo, e.g.
 * "Operation Officer VP"), the sender flips that title to "Entra is wrong" and it is listed separately
 * for the Entra owner instead.
 */

import { useMemo, useState } from 'react';
import type { OperatorCatalogIssueRow } from '@retail-ops/api-client';

interface Props {
  issues: ReadonlyArray<OperatorCatalogIssueRow>;
  onClose: () => void;
}

/** "Core Director title 'X' does not match Entra 'Y'…" or the older "SP says … 'X'; Entra JobTitle is 'Y'". */
function titles(i: OperatorCatalogIssueRow): { core: string; entra: string } {
  const core = (i.spValue ?? '').trim();
  const entra = (i.authoritativeValue ?? '').trim();
  return { core, entra };
}

function personName(email: string | null): string {
  if (!email) return '';
  const local = email.split('@')[0] ?? '';
  return local.split(/[._-]/).filter(Boolean).map((w) => w[0]!.toUpperCase() + w.slice(1)).join(' ');
}

/** Duplicate groups carry each row as "OperatorNumber=… Name='…' Email='…'" joined by " · ". */
function duplicateNames(i: OperatorCatalogIssueRow): string[] {
  return (i.spValue ?? '').split(' · ').map((part) => {
    const name = /Name='([^']*)'/.exec(part)?.[1];
    const num = /OperatorNumber=([^\s]+)/.exec(part)?.[1];
    return [name && name !== '?' ? name : null, num && num !== '?' ? `operator ${num}` : null].filter(Boolean).join(', ');
  }).filter((s) => s.length > 0);
}

export function CatalogIssuesTeamsMessage({ issues, onClose }: Props): JSX.Element {
  const [include, setInclude] = useState<ReadonlySet<number>>(new Set(issues.map((i) => i.issueId)));
  const [entraWrong, setEntraWrong] = useState<ReadonlySet<number>>(new Set());
  const [to, setTo] = useState('Scott and Amy');
  const [copied, setCopied] = useState(false);

  const toggle = (set: ReadonlySet<number>, id: number): Set<number> => {
    const n = new Set(set);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  };

  const text = useMemo(() => {
    const chosen = issues.filter((i) => include.has(i.issueId));
    const dupes = chosen.filter((i) => i.kind === 'DuplicateInitials');
    const titleIssues = chosen.filter((i) => i.kind === 'TitleMismatch');
    const fixInCore = titleIssues.filter((i) => !entraWrong.has(i.issueId));
    const fixInEntra = titleIssues.filter((i) => entraWrong.has(i.issueId));
    const other = chosen.filter((i) => i.kind !== 'DuplicateInitials' && i.kind !== 'TitleMismatch');

    const lines: string[] = [];
    lines.push(`${to.trim() || 'Hi'} — operator record cleanup in Core Director, please.`);
    lines.push('');
    lines.push('Entra (our directory) is the source of truth for each person’s name, title and e-mail. Core Director has to match it exactly, and the Core Operator Numbers list on the SystemOps SharePoint site is updated after Core so the two stay in step.');
    let n = 1;
    if (dupes.length > 0) {
      lines.push('');
      lines.push(`${n++}. Two people share the same operator initials. Initials have to be unique, so in Core Director give one person in each pair new initials, then change that person’s initials in the SharePoint list:`);
      for (const d of dupes) {
        const names = duplicateNames(d);
        lines.push(`   • ${d.operatorInitials ?? '?'} — ${names.length > 0 ? names.join(' and ') : 'two operators'}`);
      }
    }
    if (fixInCore.length > 0) {
      lines.push('');
      lines.push(`${n++}. Operator titles that don’t match Entra. In Core Director, change the operator’s title to exactly the new title shown (same words, same order), then make the same change in the SharePoint list:`);
      for (const t of fixInCore) {
        const { core, entra } = titles(t);
        lines.push(`   • ${t.operatorInitials ?? '?'} — ${personName(t.emailAddress)}: "${core}" → "${entra}"`);
      }
    }
    if (fixInEntra.length > 0) {
      lines.push('');
      lines.push(`${n++}. For IT — Entra has the title wrong (Core is correct). Update Entra; nothing to change in Core:`);
      for (const t of fixInEntra) {
        const { core, entra } = titles(t);
        lines.push(`   • ${t.operatorInitials ?? '?'} — ${personName(t.emailAddress)}: Entra says "${entra}", should be "${core}"`);
      }
    }
    if (other.length > 0) {
      lines.push('');
      lines.push(`${n++}. Other differences between Core Director and Entra (Entra governs):`);
      for (const o of other) lines.push(`   • ${o.operatorInitials ?? '?'} — ${o.detail ?? ''}`);
    }
    lines.push('');
    lines.push('When you’re done, reply here and I’ll refresh the check — anything fixed drops off the list automatically.');
    return lines.join('\n');
  }, [issues, include, entraWrong, to]);

  const copy = async (): Promise<void> => {
    try { await navigator.clipboard.writeText(text); setCopied(true); window.setTimeout(() => setCopied(false), 2500); }
    catch { setCopied(false); }
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div className="modal modal--md" role="dialog" aria-modal="true" aria-label="Teams message" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 980, width: '95vw' }}>
        <div className="modal__header"><h3>Teams message for SysOps</h3></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) minmax(320px, 1.3fr)', gap: 16, maxHeight: '70vh' }}>
          <div style={{ overflowY: 'auto' }}>
            <label style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
              To{' '}
              <input value={to} onChange={(e) => setTo(e.target.value)} style={{ width: '70%' }} />
            </label>
            <p style={{ fontSize: 12, margin: '0 0 6px' }}>Tick what to include. For a title, tick “Entra is wrong” when Core has it right.</p>
            {issues.map((i) => (
              <div key={i.issueId} style={{ fontSize: 12, padding: '4px 0', borderBottom: '1px solid #eef2f7' }}>
                <label>
                  <input type="checkbox" checked={include.has(i.issueId)} onChange={() => setInclude((s) => toggle(s, i.issueId))} />{' '}
                  <b>{i.operatorInitials ?? '?'}</b> {i.kind === 'DuplicateInitials' ? 'duplicate initials' : i.kind === 'TitleMismatch' ? 'title' : i.kind}
                  {i.emailAddress ? ` — ${personName(i.emailAddress)}` : ''}
                </label>
                {i.kind === 'TitleMismatch' && include.has(i.issueId) && (
                  <label style={{ marginLeft: 20, display: 'block', color: '#64748b' }}>
                    <input type="checkbox" checked={entraWrong.has(i.issueId)} onChange={() => setEntraWrong((s) => toggle(s, i.issueId))} /> Entra is wrong (Core is right)
                  </label>
                )}
              </div>
            ))}
          </div>
          <textarea readOnly value={text} style={{ width: '100%', minHeight: 420, fontFamily: 'inherit', fontSize: 13, padding: 10, boxSizing: 'border-box' }} aria-label="Message to copy" />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button type="button" className="btn btn--ghost" onClick={onClose}>Close</button>
          <button type="button" className="btn btn--primary" onClick={() => void copy()}>{copied ? 'Copied ✓' : 'Copy message'}</button>
        </div>
      </div>
    </div>
  );
}
